import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Envelope, Phone } from '@phosphor-icons/react';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const isRTL = i18n.language === 'ar';

  const footerLinks = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('about') },
    { path: '/industries', label: t('industries') },
    { path: '/materials', label: t('materials') },
    { path: '/process', label: t('process') },
    { path: '/gallery', label: t('gallery') },
    { path: '/contact', label: t('contact') },
  ];

  return (
    <footer className="bg-[#1A1A18] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand Section */}
          <div className="md:col-span-5">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-16 h-16 bg-[#C4A661] flex items-center justify-center text-white font-bold text-2xl">
                DS
              </div>
              <div className="font-heading text-3xl font-light tracking-tight text-white">
                {isRTL ? 'دار الصفاء' : 'Dar Al Safaa'}
              </div>
            </div>
            <p className={`text-base leading-relaxed text-gray-400 mb-8 ${
              isRTL ? 'font-arabic' : ''
            }`}>
              {isRTL 
                ? 'حيث يصبح الحجر فنًا' 
                : 'WHERE STONE BECOMES ART'
              }
            </p>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin size={24} weight="light" className="text-[#C4A661] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {t('location')}
                  </p>
                  <p className={`text-sm text-gray-300 ${
                    isRTL ? 'font-arabic' : ''
                  }`}>
                    {t('locationText')}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Envelope size={24} weight="light" className="text-[#C4A661] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {t('emailLabel')}
                  </p>
                  <a href="mailto:operations@dsmarbles.ae" className="text-sm text-gray-300 hover:text-[#C4A661] transition-colors">
                    operations@dsmarbles.ae
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone size={24} weight="light" className="text-[#C4A661] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {t('phoneLabel')}
                  </p>
                  <a href="tel:+97168048154" className="text-sm text-gray-300 hover:text-[#C4A661] transition-colors">
                    +971 6 804 8154
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <h3 className={`text-xl font-medium mb-6 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`}>
              {t('quickLinks')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`text-sm text-gray-400 hover:text-[#C4A661] transition-colors ${
                      isRTL ? 'font-arabic' : ''
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Section */}
          <div className="md:col-span-4">
            <h3 className={`text-2xl font-light mb-4 ${
              isRTL ? 'font-arabic' : 'font-heading'
            }`}>
              {isRTL ? 'ابدأ مشروعك' : 'Start Your Project'}
            </h3>
            <p className={`text-sm text-gray-400 mb-6 leading-relaxed ${
              isRTL ? 'font-arabic' : ''
            }`}>
              {isRTL
                ? 'تواصل معنا اليوم للحصول على عرض أسعار مجاني'
                : 'Contact us today for a free consultation and quote'
              }
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta-button"
              className="inline-block px-8 py-4 bg-[#C4A661] text-white text-sm font-medium hover:bg-white hover:text-[#0F0F0F] transition-all duration-300"
            >
              {t('getQuote')}
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className={`text-sm text-gray-500 ${
              isRTL ? 'font-arabic' : ''
            }`}>
              © {currentYear} Dar Al Safaa Marbles Industries LLC. {t('allRightsReserved')}.
            </p>
            <div className="flex items-center space-x-2 text-xs text-gray-600">
              <span>{isRTL ? 'موقع الويب' : 'Website'}:</span>
              <a href="https://www.dsmarbles.ae" target="_blank" rel="noopener noreferrer" className="hover:text-[#C4A661] transition-colors">
                www.dsmarbles.ae
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
